import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { getBrowserTimeZone, type TimeZoneSource } from '@/utils/timezone'

const TIMEZONE_STORAGE_KEY = 'era-timezone'

interface TimeZoneContextValue {
  timeZone: string
  source: TimeZoneSource
  isLoading: boolean
}

const TimeZoneContext = createContext<TimeZoneContextValue>({
  timeZone: 'UTC',
  source: 'utc',
  isLoading: true,
})

export const useTimeZone = () => useContext(TimeZoneContext)

const fetchTimeZoneFromIp = async (signal?: AbortSignal) => {
  const response = await fetch('https://ipapi.co/json/', { signal })
  if (!response.ok) {
    throw new Error('Failed to fetch IP timezone')
  }
  const data = (await response.json()) as { timezone?: string }
  return data?.timezone || null
}

interface TimeZoneProviderProps {
  children: React.ReactNode
}

export const TimeZoneProvider: React.FC<TimeZoneProviderProps> = ({
  children,
}) => {
  const [timeZone, setTimeZone] = useState('UTC')
  const [source, setSource] = useState<TimeZoneSource>('utc')
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    let isMounted = true
    const browserZone = getBrowserTimeZone()

    if (browserZone) {
      setTimeZone(browserZone)
      setSource('browser')
      setIsLoading(false)
      return
    }

    const cached = (() => {
      try {
        const raw = localStorage.getItem(TIMEZONE_STORAGE_KEY)
        if (!raw) return null
        const parsed = JSON.parse(raw) as {
          timeZone?: string
          source?: TimeZoneSource
        }
        return parsed?.timeZone ? parsed : null
      } catch (error) {
        console.error('Failed to read cached timezone:', error)
        return null
      }
    })()

    if (cached?.timeZone) {
      setTimeZone(cached.timeZone)
      setSource(cached.source || 'ip')
      setIsLoading(false)
      return
    }

    const controller = new AbortController()
    const timeout = window.setTimeout(() => controller.abort(), 4000)

    fetchTimeZoneFromIp(controller.signal)
      .then(zone => {
        if (!isMounted) return
        if (zone) {
          setTimeZone(zone)
          setSource('ip')
          try {
            localStorage.setItem(
              TIMEZONE_STORAGE_KEY,
              JSON.stringify({ timeZone: zone, source: 'ip' })
            )
          } catch (error) {
            console.error('Failed to cache timezone:', error)
          }
        } else {
          setTimeZone('UTC')
          setSource('utc')
        }
      })
      .catch(error => {
        if (!isMounted) return
        console.error('Failed to resolve timezone:', error)
        setTimeZone('UTC')
        setSource('utc')
      })
      .finally(() => {
        if (!isMounted) return
        window.clearTimeout(timeout)
        setIsLoading(false)
      })

    return () => {
      isMounted = false
      controller.abort()
      window.clearTimeout(timeout)
    }
  }, [])

  const value = useMemo<TimeZoneContextValue>(
    () => ({
      timeZone,
      source,
      isLoading,
    }),
    [timeZone, source, isLoading]
  )

  return (
    <TimeZoneContext.Provider value={value}>
      {children}
    </TimeZoneContext.Provider>
  )
}

export default TimeZoneProvider
